import { useEffect, useMemo, useState } from "react";
import { MediaShield, Watermark } from "@/components/site/Protect";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Copy, Facebook, Frame, Maximize2, Share2, Twitter } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useFetch } from "@/lib/hooks";
import { useSeo, imageLd, crumbsLd, seoCopy, SITE } from "@/lib/seo";
import { BACKEND, media, srcSet } from "@/lib/api";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { PhotoCard } from "@/components/site/PhotoCard";
import { useViewer } from "@/components/site/Viewer";

const ShareBar = ({ photo, title }) => {
  const { t, lang } = useLang();
  const url = `${BACKEND}/api/share/photo/${photo.slug}${lang === "fr" ? "?lang=fr" : ""}`;
  const enc = encodeURIComponent(url);
  const txt = encodeURIComponent(`${title} — MROUE CANVAS`);
  const links = [
    ["whatsapp", `https://wa.me/?text=${txt}%20${enc}`, "WhatsApp"],
    ["facebook", `https://www.facebook.com/sharer/sharer.php?u=${enc}`, <Facebook key="f" size={15} />],
    ["x", `https://twitter.com/intent/tweet?url=${enc}&text=${txt}`, <Twitter key="x" size={15} />],
    ["pinterest", `https://pinterest.com/pin/create/button/?url=${enc}&media=${encodeURIComponent(`${BACKEND}/api/og/photo/${photo.slug}.jpg`)}&description=${txt}`, "Pinterest"],
  ];
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(t("copied")); } catch { toast.error(url); }
  };
  const native = () => navigator.share?.({ title, url }).catch(() => {});
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="photo-share-bar">
      <span className="kicker mr-2 flex items-center gap-2"><Share2 size={12} /> {t("share")}</span>
      {links.map(([k, href, label]) => (
        <a key={k} href={href} target="_blank" rel="noreferrer" data-testid={`share-${k}`}
          className="flex h-10 min-w-10 items-center justify-center border border-white/15 px-3 font-mono text-[11px] tracking-wider text-white/85 transition-colors hover:border-white hover:text-white">{label}</a>
      ))}
      <button onClick={copy} data-testid="share-copy-link" className="flex h-10 items-center gap-2 border border-white/15 px-3 font-mono text-[11px] tracking-wider text-white/85 hover:border-white"><Copy size={13} /> {t("copyLink")}</button>
      {typeof navigator !== "undefined" && navigator.share && <button onClick={native} data-testid="share-native" className="flex h-10 items-center border border-white/15 px-3 text-white/85 hover:border-white"><Share2 size={14} /></button>}
    </div>
  );
};

const Meta = ({ k, v }) => (v ? <div className="border-t border-white/10 py-3"><div className="kicker !text-[10px] !text-white/50">{k}</div><div className="mt-1 font-mono text-sm text-white">{v}</div></div> : null);

export default function PhotoPage() {
  const { slug } = useParams();
  const { t, L, lang } = useLang();
  const { open } = useViewer();
  const { data: p, error } = useFetch(`/photos/${slug}`);
  const { data: siblingsData } = useFetch(p ? `/photos?category=${p.category}` : null);
  const siblings = useMemo(() => (Array.isArray(siblingsData) ? siblingsData : []), [siblingsData]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [slug]);
  const title = p ? L(p, "title") : null;
  const rawDesc = p ? L(p, "description") : "";
  const desc = rawDesc && !rawDesc.startsWith("[") ? rawDesc : `${title || ""} — ${p ? t(p.category) : ""} ${seoCopy(lang, "photoBy")}.`;
  useSeo(title, desc, p ? `${SITE}/api/og/photo/${p.slug}.jpg` : undefined, p && {
    "@graph": [
      { ...imageLd(p, L), "@id": `${SITE}/photo/${p.slug}#image`, description: desc, inLanguage: lang, isPartOf: { "@id": `${SITE}/#website` } },
      crumbsLd([["MROUE CANVAS", "/"], [t(p.category), `/gallery/${p.category}`], [title, `/photo/${p.slug}`]]),
    ],
  });
  if (error) return <div className="px-5 pt-40 text-center text-white/70" data-testid="photo-not-found">404 · <Link to="/" className="underline">MROUE CANVAS</Link></div>;
  if (!p) return <div className="min-h-screen" />;
  const idx = siblings.findIndex((x) => x.id === p.id);
  const prev = idx >= 0 ? siblings[(idx - 1 + siblings.length) % siblings.length] : null;
  const next = idx >= 0 ? siblings[(idx + 1) % siblings.length] : null;
  const related = siblings.filter((x) => x.id !== p.id).slice(0, 3);
  const d = p.variants?.display;
  const o = p.original || {};
  const exifLine = p.settings || [p.exif?.focal, p.exif?.aperture, p.exif?.shutter, p.exif?.iso].filter(Boolean).join(" · ");
  const openViewer = () => { const list = siblings.length ? siblings : [p]; open(list, Math.max(0, list.findIndex((x) => x.id === p.id))); };
  return (
    <article data-testid="photo-page">
      <section className="relative bg-[#070707] px-3 pb-6 pt-24 md:px-10 md:pt-28">
        <button onClick={openViewer} data-testid="photo-page-open-viewer" className="group relative mx-auto block w-full" style={{ maxWidth: `min(100%, calc((100vh - 9rem) * ${d ? d.width / d.height : 1.5}))` }}>
          <div className="relative w-full overflow-hidden" style={{ aspectRatio: d ? `${d.width}/${d.height}` : "3/2", backgroundColor: p.color || "#111" }}>
            {p.lqip && <img src={p.lqip} alt="" aria-hidden className={`absolute inset-0 h-full w-full scale-105 object-cover blur-2xl transition-opacity duration-700 ${loaded ? "opacity-0" : "opacity-100"}`} />}
            <img src={media(p.urls.medium)} srcSet={srcSet(p)} sizes="(max-width: 768px) 100vw, 90vw" alt={p.alt || title} onLoad={() => setLoaded(true)} data-testid="photo-page-image"
              className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-1000 ${loaded ? "opacity-100" : "opacity-0"}`} />
            <MediaShield />
            <Watermark className="absolute left-4 top-4 z-[2]" />
          </div>
          <span className="absolute bottom-4 right-4 flex items-center gap-2 border border-white/30 bg-black/50 px-3 py-2 font-mono text-[11px] tracking-wider text-white opacity-90 backdrop-blur-md transition-opacity group-hover:opacity-100"><Maximize2 size={13} /> {t("zoomDetail")}</span>
        </button>
      </section>
      <section className="mx-auto grid max-w-[1600px] gap-12 px-5 py-14 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Link to={`/gallery/${p.category}`} data-testid="photo-page-category-link" className="kicker mb-5 inline-flex items-center gap-2 !text-white/75 hover:!text-white"><ArrowLeft size={12} /> {t(p.category)}</Link>
          <MaskLines lines={[title]} className="font-display text-4xl font-extrabold leading-[0.95] text-white sm:text-6xl" testid="photo-page-title" />
          {rawDesc && <Reveal delay={0.2}><p className="mt-6 max-w-2xl text-white/75 md:text-lg" data-testid="photo-page-description">{rawDesc}</p></Reveal>}
          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={openViewer} data-testid="photo-page-zoom-button" className="btn-line"><Maximize2 size={14} /> {t("openViewer")}</button>
            <Link to={`/frame-visualizer?photo=${p.slug}`} data-testid="photo-page-visualize-link" className="btn-line"><Frame size={14} /> {t("visualize")}</Link>
          </div>
          <div className="mt-10"><ShareBar photo={p} title={title} /></div>
        </div>
        <aside className="lg:col-span-4 lg:col-start-9" data-testid="photo-page-meta">
          <Meta k={t("location")} v={p.location} />
          <Meta k={t("camera")} v={p.camera || p.exif?.camera} />
          <Meta k={t("lens")} v={p.lens || p.exif?.lens} />
          <Meta k={t("settings")} v={exifLine} />
          <Meta k={t("displayRes")} v={d && `${d.width} × ${d.height} px`} />
          <Meta k={t("originalRes")} v={o.width && `${o.width} × ${o.height} px${o.megapixels ? ` · ${o.megapixels} MP` : ""}`} />
          <Meta k="©" v={p.copyright} />
        </aside>
      </section>
      {prev && next && siblings.length > 1 && (
        <nav className="mx-auto grid max-w-[1600px] grid-cols-2 border-y border-white/10 md:px-10" data-testid="photo-page-nav">
          <Link to={`/photo/${prev.slug}`} data-testid="photo-page-prev" className="group flex items-center gap-4 px-5 py-8 md:px-0">
            <ArrowLeft className="shrink-0 text-white transition-transform group-hover:-translate-x-1" size={20} />
            <span className="min-w-0"><span className="kicker block !text-[10px]">{t("prevPhoto")}</span><span className="font-display block truncate text-lg font-bold text-white md:text-2xl">{L(prev, "title")}</span></span>
          </Link>
          <Link to={`/photo/${next.slug}`} data-testid="photo-page-next" className="group flex items-center justify-end gap-4 border-l border-white/10 px-5 py-8 text-right md:px-0">
            <span className="min-w-0"><span className="kicker block !text-[10px]">{t("nextPhoto")}</span><span className="font-display block truncate text-lg font-bold text-white md:text-2xl">{L(next, "title")}</span></span>
            <ArrowRight className="shrink-0 text-white transition-transform group-hover:translate-x-1" size={20} />
          </Link>
        </nav>
      )}
      {related.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-5 py-20 md:px-10" data-testid="photo-page-related">
          <h2 className="font-display mb-8 text-3xl font-bold text-white sm:text-4xl">{t("related")}</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.id} to={`/photo/${r.slug}`} data-testid={`related-photo-${r.slug}`} className="block aspect-[4/3]">
                <div className="pointer-events-none h-full"><PhotoCard photo={r} list={[r]} index={0} className="h-full" sizes="(max-width:640px) 100vw, 33vw" testid={`related-card-${r.slug}`} /></div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
